/**
 * SOPs Hooks
 * Jotai atoms and hooks for SOP management
 */

import { useCallback, useMemo, useRef } from 'react';
import { useAtom } from 'jotai';
import { atomWithInfiniteQuery, atomWithQuery, atomWithMutation } from 'jotai-tanstack-query';
import { invalidateQueries, queryClient } from '@/lib/query-client';
import { buScopeKey, buScopeParams } from '@/lib/business-unit-scope';
import { sopService, type SopTicketListItem } from '@/services/sop.service';
import type {
  SOP,
  SOPsListQueryParams,
  PulseCategory,
  PulseSOPStatus,
  VectorSyncResult,
  CreateSOPDto,
  SOPDecisionHistory
} from '@/types';
import { toast } from '@/components/ui/sonner';
import axios from 'axios';

// Legacy shape used only by the (UI-unreachable) /sops/new editor route.
interface UpdateSOPDto { name?: string; content?: string; status?: 'draft' | 'published'; expectedContentHash?: string; }
interface DeleteSOPDto { id: string; sopCatId?: string; }

function updatePulseSubcategoryStatus(sopCatId: string, sopStatus: PulseSOPStatus) {
  queryClient.setQueriesData<PulseCategory[]>(
    { queryKey: ['pulse', 'subcategory-counts'] },
    (previous) => {
      if (!previous) return previous;

      let changed = false;
      const next = previous.map((category) => {
        let categoryChanged = false;
        const subcategories = category.subcategories.map((subcategory) => {
          if (subcategory.sopCatId !== sopCatId || subcategory.sopStatus === sopStatus) {
            return subcategory;
          }

          changed = true;
          categoryChanged = true;
          return { ...subcategory, sopStatus };
        });

        return categoryChanged ? { ...category, subcategories } : category;
      });

      return changed ? next : previous;
    }
  );
}

// ============================================================================
// Query atoms
// ============================================================================

const sopsFilteredQueryAtom = (params?: SOPsListQueryParams) =>
  atomWithQuery((get) => ({
    queryKey: ['sops', params ?? {}, buScopeKey(get)],
    queryFn: () => sopService.getSOPs({ ...params, ...buScopeParams(get) })
  }));

const sopByIdQueryAtom = (id: string) =>
  atomWithQuery(() => ({
    queryKey: ['sops', id],
    queryFn: () => sopService.getSOPById(id),
    enabled: !!id,
    staleTime: 4.5 * 60 * 1000
  }));

/**
 * Decision history for a SOP. Only fetched when asked for (the History panel opens),
 * and a missing endpoint or no history yields an empty list rather than an error —
 * a SOP that was never generated simply has nothing to show.
 */
const sopDecisionHistoryQueryAtom = (id: string, enabled: boolean) =>
  atomWithQuery(() => ({
    queryKey: ['sops', id, 'decision-history'],
    queryFn: async (): Promise<SOPDecisionHistory> => {
      try {
        return await sopService.getDecisionHistory(id);
      } catch (error) {
        if (axios.isAxiosError(error) && (error.response?.status === 404 || error.response?.status === 501)) {
          return { runs: [] };
        }
        throw error;
      }
    },
    enabled: !!id && enabled,
    staleTime: 30 * 1000
  }));

export const sopCategoryTreeQueryAtom = atomWithQuery((get) => ({
  queryKey: ['sops', 'categorization-tree', buScopeKey(get)],
  queryFn: () => sopService.getCategoryTree(buScopeParams(get))
}));

/** The same tree, counting only drafts — what the Drafts to review sidebar lists. */
const sopDraftCategoryTreeQueryAtom = atomWithQuery((get) => ({
  queryKey: ['sops', 'categorization-tree', 'drafts', buScopeKey(get)],
  queryFn: () => sopService.getCategoryTree({ ...buScopeParams(get), drafts: 'true' })
}));

export const sopMarkdownQueryAtom = (previewUrl: string) =>
  atomWithQuery(() => ({
    // Keyed on the S3 object, not the presigned URL: every SOP refetch (the page polls while a
    // protocol builds) signs a new URL, and keying on it re-downloaded the same file each time.
    // A save still refreshes it — saves invalidate every ['sops'] query.
    queryKey: ['sops', 'markdown', previewUrl.split('?')[0]],
    queryFn: () => sopService.getMarkdownContent(previewUrl),
    enabled: !!previewUrl,
    staleTime: 4 * 60 * 1000
  }));

// The list endpoint caps limit at 100 and some accounts carry more SOPs than that.
// Back Testing reads this, so it carries the brand like every other SOP list: every
// page has to be fetched under the SAME scope or the pages would not describe one set.
export const allSopsQueryAtom = atomWithQuery((get) => ({
  queryKey: ['sops', 'all-pages', buScopeKey(get)],
  queryFn: async () => {
    const scope = buScopeParams(get);
    const first = await sopService.getSOPs({ page: 1, limit: 100, ...scope });
    // Page one reveals the count, so the rest are independent and go together.
    const rest = Array.from({ length: Math.max(0, first.pagination.totalPages - 1) }, (_, i) =>
      sopService.getSOPs({ page: i + 2, limit: 100, ...scope })
    );
    return [...first.sops, ...(await Promise.all(rest)).flatMap((page) => page.sops)];
  }
}));

// ============================================================================
// Mutation atoms
// ============================================================================

// Manual SOP creation — POST /sops { title, category, subcategory, content, version? }.
// One mutation for both entry points: the /sops/new editor and the upload dialog.
//
// Takes `get` so the create carries the selected brand: a SOP made while scoped to a
// brand belongs to that brand, not to the account. develop dropped the legacy rejecting
// `createSOPMutationAtom` in favour of this one — that removal is kept, the brand is not.
const uploadSOPMutationAtom = atomWithMutation((get) => ({
  mutationKey: ['sops', 'upload'],
  mutationFn: (data: CreateSOPDto): Promise<SOP> => sopService.createSOP(data, buScopeParams(get)),
  onSuccess: () => {
    invalidateQueries(['sops']);
    invalidateQueries(['pulse', 'subcategory-counts']);
  }
}));

const updateSOPMutationAtom = atomWithMutation(() => ({
  mutationKey: ['sops', 'update'],
  mutationFn: (variables: { id: string; data: UpdateSOPDto }): Promise<{ sop: SOP; draftPreviewUrl?: string }> =>
    sopService.updateContent(variables.id, variables.data.content ?? '', {
      expectedContentHash: variables.data.expectedContentHash,
    }),
  onSuccess: (data: { sop: SOP; draftPreviewUrl?: string }, variables: { id: string; data: UpdateSOPDto }) => {
    queryClient.setQueryData<{ sop: SOP; previewUrl: string; draftPreviewUrl?: string } | undefined>(
      ['sops', variables.id],
      (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          sop: {
            ...prev.sop,
            ...data.sop,
            publishedRevision: data.sop.publishedRevision ?? prev.sop.publishedRevision,
          },
          ...(data.draftPreviewUrl && { draftPreviewUrl: data.draftPreviewUrl }),
        };
      }
    );
    invalidateQueries(['sops']);
    invalidateQueries(['sops', variables.id]);
  }
}));

const deleteSOPMutationAtom = atomWithMutation(() => ({
  mutationKey: ['sops', 'delete'],
  mutationFn: ({ id }: DeleteSOPDto) => sopService.deleteSOP(id),
  onSuccess: (_data: void, variables: DeleteSOPDto) => {
    invalidateQueries(['sops']);
    invalidateQueries(['pulse', 'subcategory-counts']);

    if (variables.sopCatId) {
      updatePulseSubcategoryStatus(variables.sopCatId, 'none');
    }
  }
}));

const approveRejectSOPMutationAtom = atomWithMutation(() => ({
  mutationKey: ['sops', 'approveReject'],
  mutationFn: (variables: { id: string; approvalStatus: 'approved' | 'rejected' }): Promise<SOP> =>
    sopService.approveRejectSOP(variables.id, { approvalStatus: variables.approvalStatus }),
  onSuccess: (_data: SOP, variables: { id: string; approvalStatus: 'approved' | 'rejected' }) => {
    queryClient.invalidateQueries({ queryKey: ['sops'], refetchType: 'all' });
    queryClient.invalidateQueries({ queryKey: ['sops', variables.id], refetchType: 'all' });
  }
}));

function patchSOPInCaches(id: string, patch: Partial<SOP>) {
  queryClient.setQueriesData<{ sops: SOP[]; pagination: { page: number; totalPages: number; total: number } } | undefined>(
    { queryKey: ['sops'] },
    (prev) => {
      if (!prev || !Array.isArray(prev.sops)) return prev;
      let changed = false;
      const nextSops = prev.sops.map((s) => {
        if (s.id !== id) return s;
        changed = true;
        return { ...s, ...patch };
      });
      return changed ? { ...prev, sops: nextSops } : prev;
    }
  );
  queryClient.setQueryData<{ sop: SOP; previewUrl: string } | undefined>(
    ['sops', id],
    (prev) => (prev ? { ...prev, sop: { ...prev.sop, ...patch } } : prev)
  );
}

const resyncSOPMutationAtom = atomWithMutation(() => ({
  mutationKey: ['sops', 'resync'],
  mutationFn: ({ id, dryRun }: { id: string; dryRun?: boolean }): Promise<VectorSyncResult> =>
    sopService.resyncSOP(id, dryRun),
  onMutate: async ({ id }: { id: string; dryRun?: boolean }) => {
    await queryClient.cancelQueries({ queryKey: ['sops'] });
    const snapshots = queryClient.getQueriesData({ queryKey: ['sops'] });
    patchSOPInCaches(id, { vectorSyncStatus: 'syncing' });
    return { snapshots };
  },
  onSuccess: (result: VectorSyncResult) => {
    if (result.status === 'synced') toast.success('Vector sync complete');
    else if (result.status === 'skipped') toast.message(`Skipped: ${result.reason ?? 'doc no longer qualifies'}`);
    else if (result.status === 'failed') toast.error(result.error ?? 'Vector sync failed');
  },
  onError: (err: unknown, _variables, context: { snapshots: [readonly unknown[], unknown][] } | undefined) => {
    if (context?.snapshots) {
      for (const [key, data] of context.snapshots) {
        queryClient.setQueryData(key, data);
      }
    }
    let msg = 'Vector sync failed';
    if (axios.isAxiosError(err)) {
      const data = err.response?.data as { data?: { error?: string }; message?: string } | undefined;
      msg = data?.data?.error ?? data?.message ?? err.message ?? msg;
    } else if (err instanceof Error) {
      msg = err.message;
    }
    toast.error(msg);
  },
  onSettled: (_d, _e, variables) => {
    queryClient.invalidateQueries({ queryKey: ['sops'] });
    if (variables?.id) {
      queryClient.invalidateQueries({ queryKey: ['sops', variables.id] });
    }
  }
}));

export const updateSOPCategorizationMutationAtom = atomWithMutation(() => ({
  mutationKey: ['sops', 'update-categorization'],
  mutationFn: ({ id, category, subcategory }: { id: string; category: string; subcategory: string }): Promise<SOP> =>
    sopService.updateCategorization(id, category, subcategory),
  onSuccess: (_data, variables) => {
    queryClient.invalidateQueries({ queryKey: ['sops'], refetchType: 'all' });
    queryClient.invalidateQueries({ queryKey: ['sops', variables.id], refetchType: 'all' });
    invalidateQueries(['pulse', 'subcategory-counts']);
  }
}));

export const renameSOPMutationAtom = atomWithMutation(() => ({
  mutationKey: ['sops', 'rename'],
  mutationFn: ({ id, intent }: { id: string; intent: string }): Promise<SOP> => sopService.rename(id, intent),
  onSuccess: (_data, variables) => {
    queryClient.invalidateQueries({ queryKey: ['sops'], refetchType: 'all' });
    queryClient.invalidateQueries({ queryKey: ['sops', variables.id], refetchType: 'all' });
  }
}));

// ============================================================================
// Merge — N SOPs into one
// ============================================================================
// POST /api/sops/merge-batch validates, flags every SOP mergeStatus:'merging', and returns
// immediately — the actual N-1 sequential merges + rename run in the background on the server.
// This mutation only covers that quick start call; ongoing progress and outcome live on the SOP
// docs themselves (mergeStatus/mergeError), read back through the normal SOP list/detail fetches,
// not through this mutation's state. Poll ['sops'] while any row shows mergeStatus:'merging'.

export interface MergeSOPsInput {
  /** At least two ids, in selection order. sopIds[0] stays canonical and absorbs the rest. */
  sopIds: string[];
  finalName: string;
}

const mergeSOPMutationAtom = atomWithMutation(() => ({
  mutationKey: ['sops', 'merge'],
  mutationFn: (variables: { survivingId: string; mergedId: string; relationship?: 'same' | 'subtype' }) =>
    sopService.merge(variables)
}));

const mergeSOPsMutationAtom = atomWithMutation(() => ({
  mutationKey: ['sops', 'merge-many'],
  mutationFn: ({ sopIds, finalName }: MergeSOPsInput): Promise<{ survivingId: string; mergedIds: string[] }> =>
    sopService.mergeBatch({ sopIds, finalName }),
  onSuccess: (_data, variables) => {
    queryClient.invalidateQueries({ queryKey: ['sops'], refetchType: 'all' });
    for (const id of variables.sopIds) {
      queryClient.invalidateQueries({ queryKey: ['sops', id], refetchType: 'all' });
    }
    invalidateQueries(['pulse', 'subcategory-counts']);
  }
}));

// ============================================================================
// Hooks
// ============================================================================

export function useSOPs(params?: SOPsListQueryParams) {
  const filterKey = JSON.stringify(params ?? {});
  const atom = useMemo(() => sopsFilteredQueryAtom(params), [filterKey]);
  const [query] = useAtom(atom);
  return query;
}

export function useAllSOPs() {
  const [query] = useAtom(allSopsQueryAtom);
  return query;
}

const SOP_TICKETS_PAGE_SIZE = 50;

export const sopTicketsQueryAtom = (id: string) =>
  atomWithInfiniteQuery(() => ({
    queryKey: ['sops', id, 'tickets'],
    queryFn: ({ pageParam }: { pageParam?: number }) => sopService.getTickets(id, pageParam ?? 0, SOP_TICKETS_PAGE_SIZE),
    initialPageParam: 0 as number | undefined,
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.offset + lastPage.limit : undefined),
    enabled: !!id,
    retry: 1,
    staleTime: 2 * 60 * 1000
  }));

export const sopSubcategoryTicketsQueryAtom = (id: string) =>
  atomWithInfiniteQuery(() => ({
    queryKey: ['sops', id, 'subcategory-tickets'],
    queryFn: ({ pageParam }: { pageParam?: number }) =>
      sopService.getSubcategoryTickets(id, pageParam ?? 0, SOP_TICKETS_PAGE_SIZE),
    initialPageParam: 0 as number | undefined,
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.offset + lastPage.limit : undefined),
    enabled: !!id,
    retry: 1,
    staleTime: 2 * 60 * 1000
  }));

export function useSOPTickets(id: string, source: 'mapped' | 'subcategory' = 'mapped') {
  const atom = useMemo(
    () => (source === 'subcategory' ? sopSubcategoryTicketsQueryAtom(id) : sopTicketsQueryAtom(id)),
    [id, source]
  );
  const [query] = useAtom(atom);

  const tickets: SopTicketListItem[] = useMemo(
    () => query.data?.pages.flatMap((page) => page.tickets) ?? [],
    [query.data]
  );
  const hasMore = query.data?.pages[query.data.pages.length - 1]?.hasMore ?? false;
  const isFetchingNextPage = query.isFetchingNextPage;
  const queryRef = useRef(query);
  queryRef.current = query;
  const fetchNextPage = useCallback(() => {
    if (hasMore && !isFetchingNextPage) queryRef.current.fetchNextPage();
  }, [hasMore, isFetchingNextPage]);

  return {
    tickets,
    isLoading: query.isLoading,
    isError: query.isError,
    isFetchNextPageError: query.isFetchNextPageError,
    hasMore,
    isFetchingNextPage,
    fetchNextPage
  };
}


export function useSOPById(id: string) {
  const atom = useMemo(() => sopByIdQueryAtom(id), [id]);
  const [query] = useAtom(atom);
  return query;
}

/** Decision history for the History panel. Pass `enabled` so it only fetches when opened. */
export function useSOPDecisionHistory(id: string, enabled: boolean) {
  const atom = useMemo(() => sopDecisionHistoryQueryAtom(id, enabled), [id, enabled]);
  const [query] = useAtom(atom);
  return query;
}

export function useSOPMarkdown(previewUrl: string) {
  const atom = useMemo(() => sopMarkdownQueryAtom(previewUrl), [previewUrl]);
  const [query] = useAtom(atom);
  return query;
}

export function useCreateSOP() {
  const [mutation] = useAtom(uploadSOPMutationAtom);
  return mutation;
}

export function useUploadSOP() {
  const [mutation] = useAtom(uploadSOPMutationAtom);
  return mutation;
}

export function useUpdateSOP() {
  const [mutation] = useAtom(updateSOPMutationAtom);
  return mutation;
}

export function useDeleteSOP() {
  const [mutation] = useAtom(deleteSOPMutationAtom);
  return mutation;
}

export function useApproveRejectSOP() {
  const [mutation] = useAtom(approveRejectSOPMutationAtom);
  return mutation;
}

const buildProtocolMutationAtom = atomWithMutation(() => ({
  mutationKey: ['sops', 'build-protocol'],
  mutationFn: (id: string) => sopService.buildProtocol(id),
  onSuccess: (_data, id) => {
    queryClient.invalidateQueries({ queryKey: ['sops', id], refetchType: 'all' });
  }
}));

// The unlinked-SOP review: either action retires the SOP and puts its content somewhere else.
const foldSOPMutationAtom = atomWithMutation(() => ({
  mutationKey: ['sops', 'fold'],
  mutationFn: ({ id, workflowId }: { id: string; workflowId: string }) => sopService.foldIntoProtocol(id, workflowId),
  onSuccess: () => {
    invalidateQueries(['sops']);
    invalidateQueries(['workflows']);
    invalidateQueries(['protocols']);
  }
}));

const convertSOPToArticleMutationAtom = atomWithMutation(() => ({
  mutationKey: ['sops', 'article'],
  mutationFn: (id: string) => sopService.convertToArticle(id),
  onSuccess: () => {
    invalidateQueries(['sops']);
    invalidateQueries(['sources']);
  }
}));

export function useFoldSOPIntoProtocol() {
  const [mutation] = useAtom(foldSOPMutationAtom);
  return mutation;
}

export function useConvertSOPToArticle() {
  const [mutation] = useAtom(convertSOPToArticleMutationAtom);
  return mutation;
}

export function useBuildProtocol() {
  const [mutation] = useAtom(buildProtocolMutationAtom);
  return mutation;
}

export function useResyncSOP() {
  const [mutation] = useAtom(resyncSOPMutationAtom);
  return mutation;
}

export function useUpdateSOPCategorization() {
  const [mutation] = useAtom(updateSOPCategorizationMutationAtom);
  return mutation;
}

export function useRenameSOP() {
  const [mutation] = useAtom(renameSOPMutationAtom);
  return mutation;
}

/** Single pairwise merge, synchronous — exposed for callers that want to drive one step
 *  themselves. Most UI should use `useMergeSOPs` instead, which starts the background N-way
 *  merge job on the server. */
export function useMergeSOP() {
  const [mutation] = useAtom(mergeSOPMutationAtom);
  return mutation;
}

/** Starts the background batch merge. Resolves as soon as the server has validated the request
 *  and flagged every SOP 'merging' — not when the merge itself finishes. Read progress/outcome
 *  off the SOP docs (mergeStatus/mergeError), not off this mutation's state. */
export function useMergeSOPs() {
  const [mutation] = useAtom(mergeSOPsMutationAtom);
  return mutation;
}

export function useSopCategoryTree() {
  const [query] = useAtom(sopCategoryTreeQueryAtom);
  return query;
}

export function useSopDraftCategoryTree() {
  const [query] = useAtom(sopDraftCategoryTreeQueryAtom);
  return query;
}
